import { styled } from '@mui/material/styles';
import {
  Paper,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import {
  ServicePlan,
  ServicePlanServiceArea,
} from '../../../data/models/client-program.model';
import { Client } from '../../../data/models/client.model';
import ServicePlanList from './ServicePlanList';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'ClientServicePlans';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    minWidth: 400,
    maxWidth: 450,
  },

  [`& .${classes.details}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  [`& .${classes.subheader}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface ClientServicePlansProps {
  client?: Client;
  servicePlans: ServicePlan[];
  continuationToken?: string;
  fetchMoreServicePlans: (token: string) => void;
  purposeCodes: ClientPurposeCode[];
  serviceAreas: ServicePlanServiceArea[];
  transportationBenefits: TransportationBenefit[];
  servicePlanSelections: { [id: string]: boolean };
  setServicePlanSelections: (sels: { [id: string]: boolean }) => void;
  onAdd: () => void;
  onEdit: (evt: any, servicePlan: ServicePlan) => void;
  onActivate: (evt: any, servicePlan: ServicePlan) => void;
  onDeactivate: (evt: any, servicePlan: ServicePlan) => void;
  onDelete: (evt: any, servicePlan: ServicePlan) => void;
  onUnassignPurposeCode: (
    servicePlan: ServicePlan,
    purposeCode: ClientPurposeCode,
  ) => void;
  onUnassignServiceArea: (
    servicePlan: ServicePlan,
    serviceArea: ServicePlanServiceArea,
  ) => void;
  onUnassignTransportationBenefit: (
    servicePlan: ServicePlan,
    trnsportationBenefit: TransportationBenefit,
  ) => void;
  onStartDrag: (servicePlan: ServicePlan) => void;
  onEndDrag: (servicePlan: ServicePlan) => void;
  onDragEnter: (evt: any, servicePlan: ServicePlan) => void;
  onDragLeave: (servicePlan: ServicePlan) => void;
  onDrop: (servicePlan: ServicePlan) => void;
  dropServicePlanUUIDs: string[];
}

export default function ClientServicePlans({
  client,
  servicePlans,
  continuationToken,
  fetchMoreServicePlans,
  purposeCodes,
  serviceAreas,
  transportationBenefits,
  servicePlanSelections,
  setServicePlanSelections,
  onAdd,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  onUnassignPurposeCode,
  onUnassignServiceArea,
  onUnassignTransportationBenefit,
  onStartDrag,
  onEndDrag,
  onDragEnter,
  onDragLeave,
  onDrop,
  dropServicePlanUUIDs,
}: ClientServicePlansProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleSelectAllChange = (evt: any) => {
    const checked = evt.target.checked;
    if (!checked) {
      setServicePlanSelections({});
    } else {
      const selections: { [id: string]: boolean } = {};
      servicePlans.forEach((sp) => (selections[sp.servicePlanUUID!] = true));
      setServicePlanSelections(selections);
    }
  };

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.servicePlans')}
          </Typography>
          {!isSuperReaderUser && (
            <Tooltip title={t('client.addServicePlan')}>
              <span>
                <IconButton
                  data-testid="add-service-plan"
                  onClick={onAdd}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box
          className={classes.subheader}
          onDragOver={(evt) => evt.preventDefault()}
          onDragEnter={(evt) => onDragEnter(evt, servicePlans[0])}
          onDragLeave={(evt) => onDragLeave(servicePlans[0])}
          onDrop={(evt) => onDrop(servicePlans[0])}>
          {t('client.dragServicePlan')}
        </Box>
        {!isSuperReaderUser && (
          <FormControlLabel
            label={t('client.selectAllServicePlans')}
            control={<Checkbox onChange={handleSelectAllChange} />}
          />
        )}
        {client && (
          <ServicePlanList
            servicePlans={servicePlans}
            continuationToken={continuationToken}
            fetchMoreServicePlans={fetchMoreServicePlans}
            purposeCodes={purposeCodes}
            serviceAreas={serviceAreas}
            transportationBenefits={transportationBenefits}
            servicePlanSelections={servicePlanSelections}
            setServicePlanSelections={setServicePlanSelections}
            onEdit={onEdit}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
            onDelete={onDelete}
            onUnassignPurposeCode={onUnassignPurposeCode}
            onUnassignServiceArea={onUnassignServiceArea}
            onUnassignTransportationBenefit={onUnassignTransportationBenefit}
            onStartDrag={onStartDrag}
            onEndDrag={onEndDrag}
            dropServicePlanUUIDs={dropServicePlanUUIDs || []}
          />
        )}
      </Paper>
    </StyledBox>
  );
}
